import React from 'react'
import { Box, StackView } from '@planningcenter/tapestry-react'
import {
  Code,
  CodeBlock,
  Link,
  List,
  PageHeading,
  Heading,
  Text,
} from '../../../shared/typography'
import Image from '../../../shared/image'
import send_message_example from '../../../../assets/images/send_message_example.png'

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Building Your First Add-on</PageHeading>

      <List ordered>
        <List.Item>
          <Text>
            Create an OAuth Application at{' '}
            <Link
              to="https://api.planningcenteronline.com/oauth/applications"
              external
            >
              https://api.planningcenteronline.com/oauth/applications
            </Link>
            . Even if you have an existing OAuth application, we recommend you
            create a new one just for your add-on so tokens and logs are kept
            separate. It is important that the URL and Callback URLs actually
            work (you will need this for the Testing steps later).
          </Text>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={2}>
            <Text>
              Install the add-ons CLI tool (you will need Node.js and NPM
              already installed):
            </Text>

            <CodeBlock language="bash">
              {'npm install --global @planningcenter/add-ons-cli'}
            </CodeBlock>
          </StackView>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={2}>
            <Text>Create a new add-on directory:</Text>

            <CodeBlock language="bash">
              {['planningcenter-add-ons create my-add-on', 'cd my-add-on'].join(
                '\n'
              )}
            </CodeBlock>
          </StackView>
        </List.Item>

        <List.Item>
          <Text>
            Create a Personal Access Token{' '}
            <Link
              to="https://api.planningcenteronline.com/oauth/applications"
              external
            >
              here
            </Link>
            .
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Paste the token details into the <Code>.env</Code> file in your new
            add-on directory.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Update the <Code>config.yaml</Code> and <Code>.env</Code> files as
            needed to work with your OAuth Server.
          </Text>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={2}>
            <Text>Update your add-on in the cloud by running:</Text>

            <CodeBlock language="bash">
              {'planningcenter-add-ons update'}
            </CodeBlock>

            <Text>
              <em>
                You might be prompted to update your application ID in{' '}
                <Code>config.yaml</Code> when running this for the first time.
                Follow the instructions on screen.
              </em>
            </Text>
          </StackView>
        </List.Item>
      </List>

      <Heading anchored id="testing" size={1} level={2}>
        Testing Your Add-on
      </Heading>

      <List ordered>
        <List.Item>
          <Text>
            In your organization, visit the Add-ons tab in Accounts here:{' '}
            <Link
              to="https://accounts.planningcenteronline.com/organization/add-ons"
              external
            >
              https://accounts.planningcenteronline.com/organization/add-ons
            </Link>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Enable your Add-on. It should have a green "testing" badge next to
            the title.
          </Text>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={2}>
            <Text>
              Visit a List{' '}
              <Link to="https://people.planningcenteronline.com/lists" external>
                in the People product
              </Link>{' '}
              (create one if it doesn’t already exist), click the messaging
              icon, and click the “Send text with Foo” item.
            </Text>

            <Box>
              <Image
                src={send_message_example}
                style={{ marginLeft: 'unset', width: '768px' }}
              />
            </Box>
          </StackView>
        </List.Item>

        <List.Item>
          <Text>
            Your add-on should appear on the screen. Follow the instructions on
            screen to authorize it.
          </Text>
        </List.Item>
      </List>

      <Heading anchored id="revoking" size={1} level={2}>
        Revoking Your Add-on
      </Heading>

      <Text>
        As you are testing, you will likely need to test OAuth authorization
        repeatedly. To revoke the add-on token and force the user to
        re-authorize, you can visit{' '}
        <Link
          to="https://api.planningcenteronline.com/addons/authorizations"
          external
        >
          https://api.planningcenteronline.com/addons/authorizations
        </Link>
        , click on your add-on, and click "Revoke".
      </Text>
    </StackView>
  )
}

export default page
